import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';

import { supabase } from '../lib/supabase.ts';
import { analytics } from '../utils/analytics.ts';
import { googleAuth } from '../utils/googleAuth.ts';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  sessionReady: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    // 인증 상태 초기화
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setUser(session?.user ?? null);
      } catch (error) {
        console.error('세션 초기화 오류:', error);
        setUser(null);
      } finally {
        setLoading(false);
        setSessionReady(true);
      }
    };

    initializeAuth();

    // 네트워크 복구 시 세션 재확인
    const handleOnline = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error || !session) {
          const { data: refreshed } = await supabase.auth.refreshSession();
          setUser(refreshed.session?.user ?? null);
        } else {
          setUser(session.user);
        }
      } catch (error) {
        console.error('네트워크 복구 후 세션 재확인 오류:', error);
      }
    };

    window.addEventListener('online', handleOnline);

    // 인증 상태 변경 감지
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (window.location.hash.includes('access_token')) {
        window.history.replaceState(null, '', window.location.pathname);
      }

      // setLoading/setUser는 DB 작업과 무관하게 즉시 실행
      setUser(session?.user ?? null);
      setLoading(false);
      setSessionReady(true);

      // DB 작업은 비동기로 분리 (로딩 상태에 영향 없음)
      if (event === 'SIGNED_IN' && session?.user) {
        const userId = session.user.id;
        const sessionKey = `signed_in_${userId}`;

        // 이미 이번 세션에서 로그인 처리한 경우 건너뜀 (탭 복귀 시)
        if (sessionStorage.getItem(sessionKey)) return;
        sessionStorage.setItem(sessionKey, '1');

        supabase.from('users').select('id').eq('id', userId).maybeSingle()
          .then(({ data: existingUser }) => {
            if (!existingUser) {
              supabase.from('users').insert({
                id: userId,
                google_id: session.user.user_metadata?.sub,
                name: session.user.user_metadata?.full_name || session.user.email?.split('@')[0] || '',
                email: session.user.email || '',
                avatar_url: session.user.user_metadata?.avatar_url,
                last_sign_in_at: new Date().toISOString(),
                join_date: new Date().toISOString(),
                favorite_genres: [],
                bio: '',
                profile_completed: false,
              }).then(() => analytics.trackSignup('google'));
            } else {
              supabase.from('users')
                .update({ last_sign_in_at: new Date().toISOString() })
                .eq('id', userId)
                .then(() => analytics.trackLogin('google'));
            }
          })
          .catch(err => console.error('사용자 데이터 처리 오류:', err));
      }
    });

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  const signInWithGoogle = async () => {
    await googleAuth.signInWithGoogle();
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    analytics.trackEvent('logout', 'Auth', 'google');
  };

  const value = {
    user,
    loading,
    sessionReady,
    signInWithGoogle,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
